import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { X, Megaphone, ChevronRight, Bell } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { format } from "date-fns";
import { sl } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const AnnouncementPopup = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (token) {
      checkForNewAnnouncements();
    }
  }, [token]);

  const checkForNewAnnouncements = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/announcements`, { headers });
      
      if (response.data && response.data.length > 0) {
        const latest = response.data[0];
        const dismissedKey = `dismissed_announcement_${latest.id}`;
        const dismissed = localStorage.getItem(dismissedKey);
        
        if (!dismissed) {
          setLatestAnnouncement(latest);
          setShowPopup(true);
        }
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const handleDismiss = () => {
    if (latestAnnouncement) {
      localStorage.setItem(`dismissed_announcement_${latestAnnouncement.id}`, 'true');
    }
    setShowPopup(false);
  };

  const handleOpenAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogOpen(true);
    handleDismiss();
  };

  const handleViewAll = () => {
    handleDismiss();
    navigate('/obvestila');
  };

  if (!showPopup || !latestAnnouncement) return null;

  return (
    <>
      {/* Floating notification popup */}
      <div className="fixed bottom-24 right-4 z-30 animate-slide-in-right max-w-sm lg:bottom-6">
        <div className={`rounded-lg shadow-lg border-2 overflow-hidden ${
          latestAnnouncement.pomembno 
            ? 'bg-red-50 border-red-300 dark:bg-red-950/40 dark:border-red-700/50' 
            : 'bg-white border-primary/20 dark:bg-card dark:border-primary/30'
        }`}>
          {/* Header */}
          <div className={`px-4 py-2 flex items-center justify-between ${
            latestAnnouncement.pomembno 
              ? 'bg-red-100 dark:bg-red-900/40' 
              : 'bg-primary/10 dark:bg-primary/20'
          }`}>
            <div className="flex items-center gap-2">
              <Bell className={`h-4 w-4 ${latestAnnouncement.pomembno ? 'text-red-600' : 'text-primary'}`} />
              <span className="font-semibold text-sm">Novo obvestilo</span>
              {latestAnnouncement.pomembno && (
                <Badge variant="destructive" className="text-xs">Pomembno</Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-4">
            <h4 className="font-semibold text-sm mb-1 line-clamp-1">
              {latestAnnouncement.naslov}
            </h4>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {latestAnnouncement.vsebina}
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {format(new Date(latestAnnouncement.ustvarjeno), "d. MMM yyyy", { locale: sl })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={handleViewAll}
                >
                  Vsa obvestila
                </Button>
                <Button
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => handleOpenAnnouncement(latestAnnouncement)}
                >
                  Preberi
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full announcement dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              {selectedAnnouncement?.naslov}
              {selectedAnnouncement?.pomembno && (
                <Badge variant="destructive">Pomembno</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {selectedAnnouncement?.vsebina}
            </p>
            
            <div className="pt-4 border-t text-xs text-muted-foreground flex items-center justify-between">
              <span>
                Avtor: {selectedAnnouncement?.avtor_ime}
              </span>
              <span>
                {selectedAnnouncement?.ustvarjeno && 
                  format(new Date(selectedAnnouncement.ustvarjeno), "d. MMMM yyyy 'ob' HH:mm", { locale: sl })
                }
              </span>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Zapri
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnouncementPopup;
